import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { format } from "date-fns";
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography, 
    CircularProgress,
    FormControl, 
    MenuItem, 
    InputLabel, 
    Select,       
} from '@mui/material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { LAYOUT_HEIGHT, EXCLUDED_FLIGHT_STATUS } from 'state/constants';
import { filterItemByValueImmutable } from 'state/helpers';
import { 
    countFlightsByField,
    countFlightsByFieldStackedBarChart, 
} from './utils';
import Header from 'components/Header';
import ScrollToTop from 'components/ScrollToTop';
import FlexBetween from 'components/FlexBetween';
import FilterButton from 'components/FilterButton';
import ChartDialog from 'components/chartNivo/ChartDialog';
import FlightFilterDialog from 'components/FlightFilterDialog';
import SortBarChart from 'components/chartNivo/SortBarChart';
import SortStackedBarChart from 'components/chartNivo/SortStackedBarChart';
import PieChart from 'components/chartNivo/PieChart';
import { boxStyles as styles } from 'components/CustomStyles';


const viewOptions = [
  { value: 'cancelledFlightsByReason', label: 'by Reason' },
  { value: 'cancelledFlightsByDate', label: 'by Date' },
  { value: 'cancelledFlightsByDep', label: 'by Port' },
  { value: 'cancelledFlightsBySector', label: 'by Sector' },
  { value: 'cancelledFlightsBySectorType', label: 'by Sector Type' },
  { value: 'cancelledFlightsByFlightType', label: 'by Flight Type' },
  { value: 'cancelledFlightsByFlightReg', label: 'by Flight Reg' },
  { value: 'cancelledFlightsByACType', label: 'by AC Type' },
  { value: 'cancelledFlightsByCountry', label: 'by Country' },
  { value: 'cancelledFlightsByFlightNo', label: 'by Flight No' },
];


const CancelledFlightChart = () => {
    const timeMode = useSelector((state) => state.timemode);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { startDate, endDate } = useDateRangeContext();

    const scrollRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [chartOpen, setChartOpen] = useState(false); 
    const [selectedChart, setSelectedChart] = useState(null);
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState(null);
    
    const [view, setView] = useState('cancelledFlightsByReason');
    const [cancelledFlights, setCancelledFlights] = useState([]);
    
    const flightsData = useSelector((state) => state.flights); 
        
    useEffect(() => {                
        const handleFlights = () => {
            try {
                setLoading(true);

                const filtered = filterItemByValueImmutable(flightsData, 'flightStatus', EXCLUDED_FLIGHT_STATUS);                
                setCancelledFlights(filtered);                
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };        
        handleFlights();      
    }, [flightsData]);

    const filterFlights = useCallback((flights, filters) => {
        if (!Array.isArray(flights)) return [];

        if (!filters) return flights;
    
        return flights.filter((flight) => {
             const flightEtdStr = timeMode === 'UTC' ? flight.etd?.substring(0, 5) : flight.etdLocalBase?.substring(0, 5);
            const minEtd = filters.minEtd;
            const maxEtd = filters.maxEtd;

            if (minEtd && (!flightEtdStr || flightEtdStr < minEtd)) return false;
            if (maxEtd && (!flightEtdStr || flightEtdStr > maxEtd)) return false;

            return Object.entries(filters).every(([key, value]) => {
                if (!value) return true; // If no value, don't filter on this key
    
                if (key === 'minEtd' || key === 'maxEtd') return true;
                
                if (key === 'flightDate') {
                    const formattedFlightDate = flight[key] ? format(new Date(flight[key]), 'yyyy-MM-dd') : null;
                    const formattedFilterDate = value ? format(new Date(value), 'yyyy-MM-dd') : null;
                    return formattedFlightDate === formattedFilterDate;
                }
    
                if (key === 'flightNo') {
                    return flight[key] === Number(value);
                }
    
                if (key === 'acType' || key === 'flightReg') {
                    return Array.isArray(value) ? value.includes(flight[key]) : flight[key] === value;
                }
                
                // For other fields, continue normal comparison
                return flight[key] === value;
            })
        });
    }, [timeMode]);
    
    const computedViewData = useMemo(() => {
        if (!Array.isArray(cancelledFlights)) return {};

        const flightDate =
            timeMode === "UTC" ? "flightDate" : "flightDateLocalBase";

        const filteredCancelledFlights = filterFlights(cancelledFlights, filters);
        const filteredAllFlights = filterFlights(flightsData, filters);

        const byField = (field) => ({
            column: countFlightsByField(filteredCancelledFlights, field, "column"),
            stacked: countFlightsByFieldStackedBarChart(filteredAllFlights, field),
        });

        switch (view) {
            case "cancelledFlightsByReason":
                return {
                    pie: countFlightsByField(
                        filteredCancelledFlights,
                        "cancelledReason",
                        "pie"
                    ),
                    column: countFlightsByField(
                        filteredCancelledFlights,
                        "cancelledReason",
                        "column"
                    ),
                };

            case "cancelledFlightsByDate":
                return byField(flightDate);

            case "cancelledFlightsByDep":
                return byField("dep");

            case "cancelledFlightsBySector":
                return byField("sector");

            case "cancelledFlightsBySectorType":
                return byField("sectorType");

            case "cancelledFlightsByFlightType":
                return byField("flightType");

            case "cancelledFlightsByFlightReg":
                return byField("flightReg");

            case "cancelledFlightsByACType":
                return byField("acType");

            case "cancelledFlightsByCountry":
                return byField("depCountryName");

            case "cancelledFlightsByFlightNo":
                return byField("flightNo");

            default:
                return {};
        }
    }, [
        timeMode,
        view,
        filters,
        filterFlights,
        cancelledFlights,
        flightsData,
    ]);
    
    const handleFilterOpen = () => setIsFilterDialogOpen(true);
    const handleFilterClose = () => setIsFilterDialogOpen(false);
    
    const handleFilterApply = (filterValues) => {
        setFilters(filterValues); 
    };
            
    const {
        acTypeOptions,
        flightRegOptions,
        sectorTypeOptions,
        departureTypeOptions,
        arrivalTypeOptions,
    } = useMemo(() => {
        if (!Array.isArray(cancelledFlights)) {
            return {
                acTypeOptions: [],
                flightRegOptions: [],
                sectorTypeOptions: [],
                departureTypeOptions: [],
                arrivalTypeOptions: [],
            };
        }

        const sorted = (key) =>
            [...new Set(cancelledFlights.map((f) => f[key]).filter(Boolean))].sort();

        return {
            acTypeOptions: sorted("acType"),
            flightRegOptions: sorted("flightReg"),
            sectorTypeOptions: sorted("sectorType"),
            departureTypeOptions: sorted("departureType"),
            arrivalTypeOptions: sorted("arrivalType"),
        };
    }, [cancelledFlights]);
    
    const handleChartOpen = (chart) => {
        setSelectedChart(
            <Box sx={{ ...styles }}>
                <Box 
                    height="100%" 
                    width={"100%"} 
                    sx={chart.style}
                >
                    {chart.component}
                </Box>                
            </Box>
        );
        setChartOpen(true);
    };

    const handleChartClose = () => {
        setSelectedChart(null);
        setChartOpen(false);
    };

    const chartMap = useMemo(() => {
        const period = `${startDate ? format(startDate, 'ddMMMyy') : ''} - ${endDate ? format(endDate, 'ddMMMyy') : ''}`;
        const defaultTitle = `Cancelled Flight ${period}`;
        const stackedTitle = `Cancelled vs Scheduled Flight ${period}`;

        return {
            cancelledFlightsByReason: [
                {
                    component: (
                        <PieChart
                            data={computedViewData.pie}
                            title={defaultTitle}
                            topOffSet={30}
                        />
                    ),
                    style: {},
                },
                {
                    component: (
                        <SortBarChart
                            data={computedViewData.column}
                            title={defaultTitle}
                            bottomLegend="Reason"
                            leftLegend="Cancelled Flights"
                            isHorizontalBottomLegend={!isMobile || undefined}
                            digit={0}
                            bottomOffSet={!isMobile ? -30 : 30}
                            bottomLegendOffSet={!isMobile ? -30 : 30}
                        />
                    ),
                    style: {},
                },
            ],

            cancelledFlightsByDate: [
                {
                    component: (
                        <SortBarChart
                            data={computedViewData.column}
                            title={defaultTitle}
                            bottomLegend="Date"
                            leftLegend="Cancelled Flights"
                            digit={1}
                        />
                    ),
                    style: {},
                },
                {
                    component: (
                        <SortStackedBarChart
                            data={computedViewData.stacked}
                            title={stackedTitle}
                            bottomLegend="Date"
                            leftLegend="Flights"
                        />
                    ),
                    style: {},
                },
            ],

            cancelledFlightsByDep: [
                {
                    component: (
                        <SortBarChart
                            data={computedViewData.column}
                            title={defaultTitle}
                            bottomLegend="Port"
                            leftLegend="Cancelled Flights"
                            digit={1}
                        />
                    ),
                    style: {},
                },
                {
                    component: (
                        <SortStackedBarChart
                            data={computedViewData.stacked}
                            title={stackedTitle}
                            bottomLegend="Port"
                            leftLegend="Flights"
                        />
                    ),
                    style: {},
                },
            ],

            cancelledFlightsBySector: [
                {
                    component: (
                        <SortBarChart
                            data={computedViewData.column}
                            title={defaultTitle}
                            bottomLegend="Sector"
                            leftLegend="Cancelled Flights"
                            digit={1}
                            bottomOffSet={10}
                            bottomLegendOffSet={10}
                        />
                    ),
                    style: {},
                },
                {
                    component: (
                        <SortStackedBarChart
                            data={computedViewData.stacked}
                            title={stackedTitle}
                            bottomLegend="Sector"
                            leftLegend="Flights"
                            bottomOffSet={10}
                            bottomLegendOffSet={10}
                        />
                    ),
                    style: {},
                },
            ],

            cancelledFlightsBySectorType: [
                {
                    component: (
                        <SortBarChart
                            data={computedViewData.column}
                            title={defaultTitle}
                            bottomLegend="Sector Type"
                            leftLegend="Cancelled Flights"
                            digit={1}
                            isHorizontalBottomLegend
                            bottomOffSet={-30}
                            bottomLegendOffSet={-30}
                        />
                    ),
                    style: {},
                },
                {
                    component: (
                        <SortStackedBarChart
                            data={computedViewData.stacked}
                            title={stackedTitle}
                            bottomLegend="Sector Type"
                            leftLegend="Flights"
                            isHorizontalBottomLegend
                            bottomOffSet={-30}
                            bottomLegendOffSet={-30}
                        />
                    ),
                    style: {},
                },
            ],

            cancelledFlightsByFlightType: [
                {
                    component: (
                        <SortBarChart
                            data={computedViewData.column}
                            title={defaultTitle}
                            bottomLegend="Flight Type"
                            leftLegend="Cancelled Flights"
                            digit={1}
                            isHorizontalBottomLegend={!isMobile || undefined}
                            bottomOffSet={!isMobile ? -30 : 50}
                            bottomLegendOffSet={!isMobile ? -30 : 50}
                        />
                    ),
                    style: {},
                },
                {
                    component: (
                        <SortStackedBarChart
                            data={computedViewData.stacked}
                            title={stackedTitle}
                            bottomLegend="Flight Type"
                            leftLegend="Flights"
                            isHorizontalBottomLegend={!isMobile || undefined}
                            bottomOffSet={!isMobile ? -30 : 50}
                            bottomLegendOffSet={!isMobile ? -30 : 50}
                        />
                    ),
                    style: {},
                },
            ],

            cancelledFlightsByFlightReg: [
                {
                    component: (
                        <SortBarChart
                            data={computedViewData.column}
                            title={defaultTitle}
                            bottomLegend="Flight Reg"
                            leftLegend="Cancelled Flights"
                            digit={1}
                            bottomOffSet={5}
                            bottomLegendOffSet={5}
                        />
                    ),
                    style: {},
                },
                {
                    component: (
                        <SortStackedBarChart
                            data={computedViewData.stacked}
                            title={stackedTitle}
                            bottomLegend="Flight Reg"
                            leftLegend="Flights"
                            bottomOffSet={5}
                            bottomLegendOffSet={5}
                        />
                    ),
                    style: {},
                },
            ],

            cancelledFlightsByACType: [
                {
                    component: (
                        <SortBarChart
                            data={computedViewData.column}
                            title={defaultTitle}
                            bottomLegend="AC Type"
                            leftLegend="Cancelled Flights"
                            digit={1}
                        />
                    ),
                    style: {},
                },
                {
                    component: (
                        <SortStackedBarChart
                            data={computedViewData.stacked}
                            title={stackedTitle}
                            bottomLegend="AC Type"
                            leftLegend="Flights"
                        />
                    ),
                    style: {},
                },
            ],

            cancelledFlightsByCountry: [
                {
                    component: (
                        <SortBarChart
                            data={computedViewData.column}
                            title={defaultTitle}
                            bottomLegend="Country"
                            leftLegend="Cancelled Flights"
                            digit={1}
                            bottomOffSet={45}
                            bottomLegendOffSet={45}
                        />
                    ),
                    style: {},
                },
                {
                    component: (
                        <SortStackedBarChart
                            data={computedViewData.stacked}
                            title={stackedTitle}
                            bottomLegend="Country"
                            leftLegend="Flights"
                            bottomOffSet={45}
                            bottomLegendOffSet={45}
                        />
                    ),
                    style: {},
                },
            ],

            cancelledFlightsByFlightNo: [
                {
                    component: (
                        <SortBarChart
                            data={computedViewData.column}
                            title={defaultTitle}
                            bottomLegend="Flight No"
                            leftLegend="Cancelled Flights"
                            digit={1}
                            bottomOffSet={5}
                            bottomLegendOffSet={5}
                        />
                    ),
                    style: {},
                },
                {
                    component: (
                        <SortStackedBarChart
                            data={computedViewData.stacked}
                            title={stackedTitle}
                            bottomLegend="Flight No"
                            leftLegend="Flights"
                            bottomOffSet={5}
                            bottomLegendOffSet={5}
                        />
                    ),
                    style: {},
                },
            ],
        };
    }, [startDate, endDate, isMobile, computedViewData]);

    
    const renderCharts = (charts, customStyles) => (
        <Box
            ref={scrollRef}
            height={LAYOUT_HEIGHT}  
            display="flex"
            flexDirection="column"
            gap={2}
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}
        >
            {charts?.map((chart, index) => (
                <Box
                    key={index}
                    sx={{ ...styles, ...customStyles }}
                    onDoubleClick={() => handleChartOpen(chart)}
                >
                    <Box 
                        height="100%" 
                        width={"100%"} 
                        sx={chart.style}
                    >
                        {chart.component}
                    </Box>
                </Box>
            ))}
        </Box>
    );


    return (
        <Box className="main-container-box">
            <FlexBetween>
                <Header 
                    title="Cancelled Flight" 
                    subtitle={`${loading ? 'Loading...' : 'List of Charts'}`}
                />     
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <FormControl size="small" sx={{ width: isMobile ? '8rem' : '9rem' }}>
                            <InputLabel>View</InputLabel>
                            <Select
                                label="View"
                                value={view}
                                onChange={(e) => setView(e.target.value)}
                            >
                                {viewOptions.map(({ value, label }) => (
                                    <MenuItem key={value} value={value}>
                                        {label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        
                        <FilterButton
                            filters={filters}
                            onClick={handleFilterOpen}
                        />
                    </Box>
                )}                    
            </FlexBetween>
            {loading ? (
                <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <Typography color="error">
                        Error: {error?.message || ''}
                    </Typography>
                </Box>
            ) : (
                <Box
                    display="grid"                        
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                >                    
                    {view in chartMap &&
                        renderCharts(chartMap[view], { minHeight: LAYOUT_HEIGHT })
                    }

                    <ScrollToTop scrollRef={scrollRef} />
                </Box>
            )}
 
            {isFilterDialogOpen && (
                <FlightFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    acTypeOptions={acTypeOptions} 
                    flightRegOptions={flightRegOptions}
                    sectorTypeOptions={sectorTypeOptions}
                    departureTypeOptions={departureTypeOptions}
                    arrivalTypeOptions={arrivalTypeOptions}
                    currentFilters={filters}
                />
            )}

            {chartOpen && (
                <ChartDialog
                    open={chartOpen}
                    onClose={handleChartClose}
                    chartComponent={selectedChart}
                />
            )}
        </Box>
    );
};

export default CancelledFlightChart;
